import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { COLORS, ENTITY_COLORS } from '../theme';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const QUARTER_MAP = { Q1: ['January','February','March'], Q2: ['April','May','June'], Q3: ['July','August','September'], Q4: ['October','November','December'] };

const getQuarter = (month) => {
  for (const [q, months] of Object.entries(QUARTER_MAP)) {
    if (months.includes(month)) return q;
  }
  return null;
};

const fetchData = async (sheet) => {
  const response = await fetch(`/api/getSheetData?sheet=${encodeURIComponent(sheet)}`);
  const data = await response.json();
  return data;
};

const parseRows = (rows) => {
  if (!rows || !Array.isArray(rows)) return [];
  const headers = rows[0];
  return rows.slice(1).filter(row => row[0]).map(row => {
    const obj = {};
    headers.forEach((h, i) => { obj[h] = row[i]; });
    return obj;
  });
};

const toDateValue = (row) => {
  return new Date(Number(row.Year), MONTHS.indexOf(row.Month), 1);
};

const filterByPeriod = (data, period, customFrom, customTo) => {
  const now = new Date();
  const currentYear = now.getFullYear();

  if (period === 'All Time') return data;

  if (period === 'YTD') {
    return data.filter(r => Number(r.Year) === currentYear);
  }

  if (period === '1Y') {
    const cutoff = new Date(now);
    cutoff.setMonth(cutoff.getMonth() - 12);
    return data.filter(r => toDateValue(r) >= cutoff);
  }

  if (period === '5Y') {
    const cutoff = new Date(now);
    cutoff.setFullYear(cutoff.getFullYear() - 5);
    return data.filter(r => toDateValue(r) >= cutoff);
  }

  if (period === 'Custom' && customFrom && customTo) {
    const from = new Date(customFrom.year, customFrom.month, 1);
    const to = new Date(customTo.year, customTo.month, 1);
    return data.filter(r => {
      const d = toDateValue(r);
      return d >= from && d <= to;
    });
  }

  return data;
};

const aggregateQuarterly = (data) => {
  const quarters = {};
  data.forEach(row => {
    const q = getQuarter(row.Month);
    const year = String(row.Year);
    const key = `${year}-${q}`;
    if (!quarters[key]) {
      quarters[key] = { period: `${q} ${year}`, year, quarter: q, count: 0, 'Portfolio Value': 0, 'Dividend Income': 0, 'Yield': 0, 'Leverage Ratio': 0, 'AUM': 0, 'ROE': 0 };
    }
    quarters[key]['Portfolio Value'] = row['Portfolio Value'];
    quarters[key]['AUM'] = row['AUM'];
    quarters[key]['Dividend Income'] += (row['Dividend Income'] || 0);
    quarters[key]['Yield'] += (row['Yield'] || 0);
    quarters[key]['Leverage Ratio'] += (row['Leverage Ratio'] || 0);
    quarters[key]['ROE'] += (row['ROE'] || 0);
    quarters[key].count += 1;
  });
  return Object.values(quarters).map(q => ({
    ...q,
    'Yield': parseFloat((q['Yield'] / q.count).toFixed(2)),
    'Leverage Ratio': parseFloat((q['Leverage Ratio'] / q.count).toFixed(2)),
    'ROE': parseFloat((q['ROE'] / q.count).toFixed(2)),
  })).sort((a, b) => {
    if (a.year !== b.year) return a.year.localeCompare(b.year);
    return ['Q1','Q2','Q3','Q4'].indexOf(a.quarter) - ['Q1','Q2','Q3','Q4'].indexOf(b.quarter);
  });
};

const aggregateAnnually = (data) => {
  const years = {};
  data.forEach(row => {
    const year = String(row.Year);
    if (!years[year]) {
      years[year] = { period: year, year, count: 0, 'Portfolio Value': 0, 'Dividend Income': 0, 'Yield': 0, 'Leverage Ratio': 0, 'AUM': 0, 'ROE': 0 };
    }
    years[year]['Portfolio Value'] = row['Portfolio Value'];
    years[year]['AUM'] = row['AUM'];
    years[year]['Dividend Income'] += (row['Dividend Income'] || 0);
    years[year]['Yield'] += (row['Yield'] || 0);
    years[year]['Leverage Ratio'] += (row['Leverage Ratio'] || 0);
    years[year]['ROE'] += (row['ROE'] || 0);
    years[year].count += 1;
  });
  return Object.values(years).map(y => ({
    ...y,
    'Yield': parseFloat((y['Yield'] / y.count).toFixed(2)),
    'Leverage Ratio': parseFloat((y['Leverage Ratio'] / y.count).toFixed(2)),
    'ROE': parseFloat((y['ROE'] / y.count).toFixed(2)),
  })).sort((a, b) => a.year.localeCompare(b.year));
};

const consolidate = (fjcData, fjhData) => {
  const combined = {};
  [...fjcData, ...fjhData].forEach(row => {
    const key = `${row.Month}-${row.Year}`;
    if (!combined[key]) {
      combined[key] = { Month: row.Month, Year: row.Year, 'Portfolio Value': 0, 'Dividend Income': 0, 'Yield': 0, 'Leverage Ratio': 0, 'AUM': 0, 'ROE': 0, count: 0 };
    }
    combined[key]['Portfolio Value'] += (row['Portfolio Value'] || 0);
    combined[key]['Dividend Income'] += (row['Dividend Income'] || 0);
    combined[key]['Yield'] += (row['Yield'] || 0);
    combined[key]['Leverage Ratio'] += (row['Leverage Ratio'] || 0);
    combined[key]['AUM'] += (row['AUM'] || 0);
    combined[key]['ROE'] += (row['ROE'] || 0);
    combined[key].count += 1;
  });
  return Object.values(combined).map(row => ({
    ...row,
    'Yield': parseFloat((row['Yield'] / row.count).toFixed(2)),
    'Leverage Ratio': parseFloat((row['Leverage Ratio'] / row.count).toFixed(2)),
    'ROE': parseFloat((row['ROE'] / row.count).toFixed(2)),
  })).sort((a, b) => {
    if (String(a.Year) !== String(b.Year)) return String(a.Year).localeCompare(String(b.Year));
    return MONTHS.indexOf(a.Month) - MONTHS.indexOf(b.Month);
  });
};

// Metrics priced in dollars show a % change; ratio/percent metrics show a point change.
const METRICS = [
  { label: 'Portfolio Value', key: 'Portfolio Value', prefix: '$', deltaType: 'percent' },
  { label: 'Dividend Income', key: 'Dividend Income', prefix: '$', deltaType: 'percent' },
  { label: 'Yield', key: 'Yield', suffix: '%', deltaType: 'point' },
  { label: 'Leverage Ratio', key: 'Leverage Ratio', suffix: 'x', deltaType: 'point' },
  { label: 'AUM', key: 'AUM', prefix: '$', deltaType: 'percent' },
  { label: 'ROE', key: 'ROE', suffix: '%', deltaType: 'point' },
];

const computeDelta = (metric, latest, previous) => {
  if (!latest || !previous) return null;
  const curr = latest[metric.key];
  const prev = previous[metric.key];
  if (typeof curr !== 'number' || typeof prev !== 'number') return null;
  if (metric.deltaType === 'percent') {
    if (!prev) return null;
    return { display: `${(((curr - prev) / prev) * 100).toFixed(1)}%`, positive: curr - prev >= 0 };
  }
  const diff = curr - prev;
  return { display: `${Math.abs(diff).toFixed(2)}${metric.suffix || ''}`, positive: diff >= 0 };
};

const Dashboard = () => {
  const [fjcData, setFjcData] = useState([]);
  const [fjhData, setFjhData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [entity, setEntity] = useState('Consolidated');
  const [viewMode, setViewMode] = useState('Monthly');
  const [period, setPeriod] = useState('All Time');
  const [customFrom, setCustomFrom] = useState({ month: 0, year: 2025 });
  const [customTo, setCustomTo] = useState({ month: new Date().getMonth(), year: new Date().getFullYear() });

  useEffect(() => {
    const load = async () => {
      try {
        const [fjcRows, fjhRows] = await Promise.all([
          fetchData('FJC Dashboard'),
          fetchData('FJH Dashboard'),
        ]);
        setFjcData(parseRows(fjcRows));
        setFjhData(parseRows(fjhRows));
      } catch (e) {
        setError('Failed to load dashboard data.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const getActiveData = () => {
    if (entity === 'FJC') return fjcData;
    if (entity === 'FJH') return fjhData;
    return consolidate(fjcData, fjhData);
  };

  const getChartData = () => {
    const active = getActiveData();
    const filtered = filterByPeriod(active, period, customFrom, customTo);

    if (viewMode === 'Monthly') {
      return filtered
        .sort((a, b) => {
          if (String(a.Year) !== String(b.Year)) return String(a.Year).localeCompare(String(b.Year));
          return MONTHS.indexOf(a.Month) - MONTHS.indexOf(b.Month);
        })
        .map(r => ({ ...r, period: `${r.Month.slice(0, 3)} '${String(r.Year).slice(2)}` }));
    }
    if (viewMode === 'Quarterly') {
      return aggregateQuarterly(filtered).map(r => ({ ...r, period: `${r.quarter} '${String(r.year).slice(2)}` }));
    }
    if (viewMode === 'Annually') {
      return aggregateAnnually(filtered).map(r => ({ ...r, period: r.year }));
    }
    return [];
  };

  const chartData = getChartData();
  const latest = chartData[chartData.length - 1];
  const previous = chartData[chartData.length - 2];
  const angled = true;
  const interval = viewMode === 'Monthly' ? 2 : 0;

  const xAxisProps = {
    dataKey: 'period',
    tick: { fontFamily: 'Georgia, serif', fontSize: 9, dy: 10 },
    interval,
    angle: angled ? -45 : 0,
    textAnchor: angled ? 'end' : 'middle',
    height: angled ? 60 : 30,
  };

  const availableYears = [...new Set(getActiveData().map(r => Number(r.Year)))].sort();
  const minYear = availableYears[0] || new Date().getFullYear();
  const maxYear = availableYears[availableYears.length - 1] || new Date().getFullYear();

  // Consolidated keeps the classic navy/slate two-tone; a specific entity tints every chart with its accent.
  const primaryColor = entity === 'Consolidated' ? COLORS.navy : ENTITY_COLORS[entity];
  const secondaryColor = entity === 'Consolidated' ? COLORS.slate : ENTITY_COLORS[entity];

  if (loading) return <div style={styles.loading}>Loading financial data...</div>;
  if (error) return <div style={styles.loading}>{error}</div>;

  return (
    <div style={styles.container}>
      <style>{DASHBOARD_STYLES}</style>

      <div style={styles.header}>
        <h1 style={styles.title}>Financial Dashboard</h1>
        <div style={styles.divider} />
        <p style={styles.subtitle}>
          Interactive view of Flying Jib's financial performance.
          Toggle between entities, time periods, and date ranges to analyze the data.
        </p>
      </div>

      {/* Controls */}
      <div style={styles.controls} className="fj-card">
        <div style={styles.controlGroup}>
          <span style={styles.controlLabel}>Entity</span>
          <div style={styles.buttonGroup}>
            {['Consolidated', 'FJH', 'FJC'].map(e => (
              <button
                key={e}
                onClick={() => setEntity(e)}
                className="fj-entity-button"
                style={entity === e
                  ? { ...styles.activeButton, backgroundColor: ENTITY_COLORS[e], borderColor: ENTITY_COLORS[e] }
                  : { ...styles.inactiveButton, color: ENTITY_COLORS[e], borderColor: ENTITY_COLORS[e] }}
              >{e}</button>
            ))}
          </div>
        </div>

        <div style={styles.controlGroup}>
          <span style={styles.controlLabel}>View</span>
          <div style={styles.buttonGroup}>
            {['Monthly', 'Quarterly', 'Annually'].map(v => (
              <button key={v} onClick={() => setViewMode(v)} style={viewMode === v ? styles.activeButton : styles.inactiveButton}>{v}</button>
            ))}
          </div>
        </div>

        <div style={styles.controlGroup}>
          <span style={styles.controlLabel}>Period</span>
          <div style={styles.buttonGroup}>
            {['All Time', '5Y', '1Y', 'YTD', 'Custom'].map(p => (
              <button key={p} onClick={() => setPeriod(p)} style={period === p ? styles.activeButton : styles.inactiveButton}>{p}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Custom Date Range */}
      {period === 'Custom' && (
        <div style={styles.customRange} className="fj-card">
          <span style={styles.controlLabel}>From</span>
          <select
            style={styles.select}
            value={customFrom.month}
            onChange={e => setCustomFrom(prev => ({ ...prev, month: Number(e.target.value) }))}
          >
            {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
          </select>
          <select
            style={styles.select}
            value={customFrom.year}
            onChange={e => setCustomFrom(prev => ({ ...prev, year: Number(e.target.value) }))}
          >
            {Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i).map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>

          <span style={{ ...styles.controlLabel, marginLeft: '16px' }}>To</span>
          <select
            style={styles.select}
            value={customTo.month}
            onChange={e => setCustomTo(prev => ({ ...prev, month: Number(e.target.value) }))}
          >
            {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
          </select>
          <select
            style={styles.select}
            value={customTo.year}
            onChange={e => setCustomTo(prev => ({ ...prev, year: Number(e.target.value) }))}
          >
            {Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i).map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      )}

      {/* Stat Boxes */}
      <div style={styles.statsRow}>
        {METRICS.map(metric => {
          const value = latest ? latest[metric.key] : '--';
          const delta = computeDelta(metric, latest, previous);
          return (
            <div key={metric.label} style={{ ...styles.statBox, borderTopColor: primaryColor }} className="fj-card">
              <span style={styles.statLabel}>{metric.label}</span>
              <span style={styles.statValue}>
                {metric.prefix || ''}{typeof value === 'number' ? value.toLocaleString() : value}{metric.suffix || ''}
              </span>
              {delta && (
                <span style={{ ...styles.statDelta, color: delta.positive ? COLORS.positive : COLORS.negative }}>
                  {delta.positive ? '▲' : '▼'} {delta.display}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Charts Row 1 */}
      <div style={styles.chartsRow}>
        <div style={{ ...styles.chartCard, borderLeftColor: primaryColor }} className="fj-card">
          <h3 style={styles.chartTitle}>Portfolio Value</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e8f0" />
              <XAxis {...xAxisProps} />
              <YAxis tick={{ fontFamily: 'Georgia, serif', fontSize: 11 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={v => [`$${Number(v).toLocaleString()}`, 'Portfolio Value']} />
              <Line type="monotone" dataKey="Portfolio Value" stroke={primaryColor} strokeWidth={2} dot={chartData.length > 24 ? false : { fill: primaryColor }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div style={{ ...styles.chartCard, borderLeftColor: primaryColor }} className="fj-card">
          <h3 style={styles.chartTitle}>Dividend Income {viewMode !== 'Monthly' ? '(Summed)' : ''}</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e8f0" />
              <XAxis {...xAxisProps} />
              <YAxis tick={{ fontFamily: 'Georgia, serif', fontSize: 11 }} tickFormatter={v => `$${v}`} />
              <Tooltip formatter={v => [`$${Number(v).toLocaleString()}`, 'Dividend Income']} />
              <Bar dataKey="Dividend Income" fill={primaryColor} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={{ ...styles.chartCard, borderLeftColor: primaryColor }} className="fj-card">
          <h3 style={styles.chartTitle}>Yield % {viewMode !== 'Monthly' ? '(Avg)' : ''}</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e8f0" />
              <XAxis {...xAxisProps} />
              <YAxis tick={{ fontFamily: 'Georgia, serif', fontSize: 11 }} tickFormatter={v => `${v}%`} />
              <Tooltip formatter={v => [`${v}%`, 'Yield']} />
              <Line type="monotone" dataKey="Yield" stroke={primaryColor} strokeWidth={2} dot={chartData.length > 24 ? false : { fill: primaryColor }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div style={styles.chartsRow}>
        <div style={{ ...styles.chartCard, borderLeftColor: secondaryColor }} className="fj-card">
          <h3 style={styles.chartTitle}>AUM</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e8f0" />
              <XAxis {...xAxisProps} />
              <YAxis tick={{ fontFamily: 'Georgia, serif', fontSize: 11 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={v => [`$${Number(v).toLocaleString()}`, 'AUM']} />
              <Line type="monotone" dataKey="AUM" stroke={secondaryColor} strokeWidth={2} dot={chartData.length > 24 ? false : { fill: secondaryColor }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div style={{ ...styles.chartCard, borderLeftColor: secondaryColor }} className="fj-card">
          <h3 style={styles.chartTitle}>Leverage Ratio {viewMode !== 'Monthly' ? '(Avg)' : ''}</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e8f0" />
              <XAxis {...xAxisProps} />
              <YAxis tick={{ fontFamily: 'Georgia, serif', fontSize: 11 }} tickFormatter={v => `${v}x`} />
              <Tooltip formatter={v => [`${v}x`, 'Leverage Ratio']} />
              <Bar dataKey="Leverage Ratio" fill={secondaryColor} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={{ ...styles.chartCard, borderLeftColor: secondaryColor }} className="fj-card">
          <h3 style={styles.chartTitle}>ROE % {viewMode !== 'Monthly' ? '(Avg)' : ''}</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e8f0" />
              <XAxis {...xAxisProps} />
              <YAxis tick={{ fontFamily: 'Georgia, serif', fontSize: 11 }} tickFormatter={v => `${v}%`} />
              <Tooltip formatter={v => [`${v}%`, 'ROE']} />
              <Line type="monotone" dataKey="ROE" stroke={secondaryColor} strokeWidth={2} dot={chartData.length > 24 ? false : { fill: secondaryColor }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div style={styles.disclaimer}>
        <p style={styles.disclaimerText}>
          Financial figures are derived from end-of-month account statements and reflect balances and performance metrics as of the last business day of each reporting period. Data is updated monthly following receipt of official account statements and may not reflect current market values or intraday fluctuations. Dashboard values may not align precisely with portfolio values displayed elsewhere on this site due to differences in reporting timing, intraday market fluctuations, and trades executed during the current period. Past performance is not indicative of future results. This information is provided for informational purposes only and does not constitute investment advice. Flying Jib Capital Inc. is not a registered investment adviser.
        </p>
      </div>
    </div>
  );
};

const DASHBOARD_STYLES = `
  .fj-card {
    transition: box-shadow 0.3s ease, transform 0.3s ease;
  }
  .fj-card:hover {
    box-shadow: 0 12px 28px rgba(10, 22, 40, 0.10);
  }
  .fj-entity-button {
    transition: background-color 0.25s ease, color 0.25s ease, opacity 0.25s ease;
  }
  .fj-entity-button:hover {
    opacity: 0.8;
  }
`;

const styles = {
  container: { minHeight: '90vh', backgroundColor: COLORS.bg, padding: '60px 60px' },
  loading: { minHeight: '90vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Georgia, serif', fontSize: '18px', color: COLORS.navy, backgroundColor: COLORS.bg },
  header: { maxWidth: '1400px', margin: '0 auto 40px auto', textAlign: 'center' },
  title: { fontSize: '42px', fontWeight: '700', color: COLORS.navy, fontFamily: 'Georgia, serif', letterSpacing: '1px', marginBottom: '16px' },
  divider: { width: '60px', height: '3px', backgroundColor: COLORS.navy, margin: '0 auto 24px auto' },
  subtitle: { fontSize: '16px', color: COLORS.slate, fontFamily: 'Georgia, serif', lineHeight: '1.8' },
  controls: { maxWidth: '1400px', margin: '0 auto 16px auto', backgroundColor: '#ffffff', padding: '24px 32px', borderLeft: `3px solid ${COLORS.navy}`, boxShadow: '0 1px 3px rgba(10, 22, 40, 0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' },
  customRange: { maxWidth: '1400px', margin: '0 auto 24px auto', backgroundColor: '#ffffff', padding: '20px 32px', borderLeft: `3px solid ${COLORS.navy}`, boxShadow: '0 1px 3px rgba(10, 22, 40, 0.06)', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' },
  controlGroup: { display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' },
  controlLabel: { fontSize: '13px', color: COLORS.navy, fontFamily: 'Georgia, serif', letterSpacing: '1px', textTransform: 'uppercase', fontWeight: '700' },
  buttonGroup: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  activeButton: { backgroundColor: COLORS.navy, color: '#ffffff', border: `1px solid ${COLORS.navy}`, padding: '8px 14px', fontFamily: 'Georgia, serif', fontSize: '13px', cursor: 'pointer', letterSpacing: '0.5px' },
  inactiveButton: { backgroundColor: 'transparent', color: COLORS.navy, border: `1px solid ${COLORS.navy}`, padding: '8px 14px', fontFamily: 'Georgia, serif', fontSize: '13px', cursor: 'pointer', letterSpacing: '0.5px' },
  select: { padding: '8px 12px', fontFamily: 'Georgia, serif', fontSize: '13px', color: COLORS.navy, border: `1px solid ${COLORS.navy}`, backgroundColor: '#ffffff', cursor: 'pointer' },
  statsRow: { maxWidth: '1400px', margin: '0 auto 32px auto', display: 'flex', gap: '16px', flexWrap: 'wrap' },
  statBox: { backgroundColor: '#ffffff', padding: '20px 24px', borderTop: '3px solid', boxShadow: '0 1px 3px rgba(10, 22, 40, 0.06)', display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: '140px' },
  statLabel: { fontSize: '11px', color: COLORS.slate, fontFamily: 'Georgia, serif', letterSpacing: '1px', textTransform: 'uppercase' },
  statValue: { fontSize: '20px', fontWeight: '700', color: COLORS.navy, fontFamily: 'Georgia, serif' },
  statDelta: { fontSize: '11px', fontFamily: 'Georgia, serif', fontWeight: '700', letterSpacing: '0.3px' },
  chartsRow: { maxWidth: '1400px', margin: '0 auto 24px auto', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '24px' },
  chartCard: { backgroundColor: '#ffffff', padding: '24px', borderLeft: '3px solid', boxShadow: '0 1px 3px rgba(10, 22, 40, 0.06)' },
  chartTitle: { fontSize: '16px', fontWeight: '700', color: COLORS.navy, fontFamily: 'Georgia, serif', marginBottom: '16px', letterSpacing: '0.5px' },
  disclaimer: { maxWidth: '1400px', margin: '0 auto', borderTop: '1px solid #d0d8e4', paddingTop: '24px' },
  disclaimerText: { fontSize: '12px', color: COLORS.slate, fontFamily: 'Georgia, serif', lineHeight: '1.8', textAlign: 'center', margin: 0 },
};

export default Dashboard;
